import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createPledgeReleaseCase,
  getPledgeReleaseCaseById,
  updatePledgeReleaseCase,
} from '../api';
import { fetchCustomers, getCustomer } from '../../customers/api';
import { fetchAgents } from '../../agents/api';
import {
  Form, Input, InputNumber, DatePicker, Select, Button, Card, Typography,
  Row, Col, Spin, message, Space, Table, Divider, Tag, Tooltip, Switch, Radio, Alert,
} from 'antd';
import {
  CloseOutlined, PlusOutlined, GoldOutlined, SyncOutlined, UserOutlined, BankOutlined,
  NumberOutlined, CheckOutlined, ProfileOutlined, PhoneOutlined, TeamOutlined,
} from '@ant-design/icons';
import SavedBankAccountSelect, { useCustomerBankAutofill } from '../../customers/components/SavedBankAccountSelect';
import IfscInput, { IFSC_PATTERN, bankBranchLabel } from '../../customers/components/IfscInput';
import { FormSection, CustomerSelect, AgentSelect } from '../../../components/CaseForm';
import { fetchHallmarkGoldRate, invalidateGoldPriceCache } from '../../../services/goldPriceService';
import { fetchSilverRates } from '../../../services/silverPriceService';
import { useKaratDeductions } from '../../system/hooks/useKaratDeductions';
import dayjs from 'dayjs';
import {
  recalcCaseGoldItem, calculateCaseGoldTotals, calculateCaseProcessingFee, calculateCaseFinalAmount,
  formatWeight4, formatAmount4, calculateCaseRoundOff, formatRoundOff,
} from '../../../utils/goldCalculation';
import { usePermissions } from '../../../hooks/usePermissions';
import { useUnsavedCaseGuard, hasGoldItemInput } from '../../../hooks/useUnsavedCaseGuard';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchGstToggleVisibility, fetchBranchProcessingFeeToggleVisibility } from '../../treasury/api';
import MeltingConsentButton from '../../../components/MeltingConsentForm';
import 'bootstrap/dist/css/bootstrap.min.css';

const { Title, Text } = Typography;

const STATUS_OPTIONS = [
  'LEAD_IDENTIFIED',
  'DOCS_VERIFIED',
  'ADVANCE_APPROVED',
  'PART_PAYMENT_DONE',
  'FULLY_PAID_AWAITING_RELEASE',
  'GOLD_RECEIVED',
  'VALUATION_COMPLETED',
  'BILL_GENERATED',
  'CLOSED',
];

const formatWholeAmount = (price) =>
  new Intl.NumberFormat('en-IN', { style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(price);

const formatRoundingAmount = (amount) => {
  if (amount > 0) {
    return `+₹ ${formatWholeAmount(amount)}`;
  } else if (amount < 0) {
    return `-₹ ${formatWholeAmount(Math.abs(amount))}`;
  }
  return `₹ ${formatWholeAmount(amount)}`;
};

// Processing fee is exact (not rounded to a rupee), so it shows 4 decimals.
const formatProcessingFeeDisplay = (amount) => {
  if (amount > 0) {
    return `-₹ ${formatAmount4(amount)}`;
  } else if (amount < 0) {
    return `+₹ ${formatAmount4(Math.abs(amount))}`;
  }
  return `₹ ${formatAmount4(amount)}`;
};

// Stored on verificationDetails.goldOwnership, which only accepts these.
const GOLD_OWNERSHIP_OPTIONS = ['Self', 'Spouse', 'Parents', 'Other'].map((value) => ({ value, label: value }));

const DEFAULT_PURITY = 'Gold 916'; // Default purity selection


// Silver: rates are manual (System → Silver Rates), no live feed, no karat deduction.
const SILVER_DEFAULT_PURITY = 'Silver 999';

const EMPTY_GOLD_ITEM = {
  qty: 1,
  itemName: '',
  purityName: DEFAULT_PURITY,  // Track which purity is selected
  purity: 0,  // Never prefilled — typed in after testing
  grossWeight: 0,
  stoneWaxDeduction: 0,
  dustDeduction: 0,
  netWeight: 0,
  estimatedRatePerGram: 0,
  totalPrice: 0,
};

const PledgeReleaseCaseForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { canViewCreateModule, canEditModule, canViewField, canEditField } = usePermissions();
  const { user } = useAuth();
  const { getChannelRate, loading: deductionsLoading } = useKaratDeductions();

  const canFieldCustomerShow = canEditField('pledgeReleaseCase', 'customerId') || canViewField('pledgeReleaseCase', 'customerId');
  const canFieldCustomerEdit = canEditField('pledgeReleaseCase', 'customerId');
  const canFieldLenderShow = canEditField('pledgeReleaseCase', 'lenderName') || canViewField('pledgeReleaseCase', 'lenderName');
  const canFieldLenderEdit = canEditField('pledgeReleaseCase', 'lenderName');
  const canFieldLoanAccountShow = canEditField('pledgeReleaseCase', 'loanAccountNumber') || canViewField('pledgeReleaseCase', 'loanAccountNumber');
  const canFieldLoanAccountEdit = canEditField('pledgeReleaseCase', 'loanAccountNumber');
  const canFieldClosureShow = canEditField('pledgeReleaseCase', 'closureAmount') || canViewField('pledgeReleaseCase', 'closureAmount');
  const canFieldClosureEdit = canEditField('pledgeReleaseCase', 'closureAmount');
  const canFieldStatusShow = canEditField('pledgeReleaseCase', 'status') || canViewField('pledgeReleaseCase', 'status');
  const canFieldStatusEdit = canEditField('pledgeReleaseCase', 'status');
  const canFieldGoldItemsShow = canEditField('pledgeReleaseCase', 'goldItems') || canViewField('pledgeReleaseCase', 'goldItems');
  const canFieldGoldItemsEdit = canEditField('pledgeReleaseCase', 'goldItems');

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  // Full customer doc behind the selected id — the melting-consent form needs
  // address and ID proof, which the <Select> options don't carry.
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // Case identity for the consent form; blank until the case is saved.
  const [caseMeta, setCaseMeta] = useState({ code: null, date: null });
  const [messageApi, contextHolder] = message.useMessage();
  const [agents, setAgents] = useState([]);
  const [agentCommissionPercent, setAgentCommissionPercent] = useState(0);

  const [goldItems, setGoldItems] = useState([{ ...EMPTY_GOLD_ITEM }]);
  // A new case lives only in this form until Create — warn before losing it.
  const allowLeave = useUnsavedCaseGuard(!isEdit && hasGoldItemInput(goldItems), 'Pledge Release Case');
  const [processingFee, setProcessingFee] = useState(0.5);
  const [roundingUpAmount, setRoundingUpAmount] = useState(0);
  const [showGstToggle, setShowGstToggle] = useState(false);
  const [showProcessingFeeToggle, setShowProcessingFeeToggle] = useState(true);

  // Live gold rate state (stores rates with karat mappings)
  const [liveRates, setLiveRates] = useState(null);   // { rates: [{ karat, purity, perGram, per10gram }, ...], date }
  const [rateLoading, setRateLoading] = useState(false);
  const [metalType, setMetalType] = useState('GOLD');
  const [silverRates, setSilverRates] = useState(null);
  const [metalLocked, setMetalLocked] = useState(false); // true once case is batched
  // Set only when an approved reconciliation has unlocked this case for one
  // corrective save of the case details.
  const [reconciliation, setReconciliation] = useState(null);
  
  // Track deduction loading state
  const [hasInitializedDefaults, setHasInitializedDefaults] = useState(false);

  const loadLiveRate = async (silent = false) => {
    if (!silent) setRateLoading(true);
    const rateData = await fetchHallmarkGoldRate();
    if (rateData) {
      setLiveRates(rateData);
    }
    setRateLoading(false);
    return rateData;
  };

  const handleRefreshRate = async () => {
    invalidateGoldPriceCache();
    const rateData = await loadLiveRate();
    if (rateData?.rates && Array.isArray(rateData.rates)) {
      // Only update liveRates state; do NOT change existing gold item rates
      // When user changes karat, the new rates will be applied via handleGoldItemChange
      messageApi.success(`Live rates updated (${rateData.date})`);
    } else {
      messageApi.warning('Could not refresh live rates');
    }
  };

  // Fetch live rate and setup defaults (both create and edit)
  useEffect(() => {
    // Only loads the rates. The default row's rate is filled in by the effect
    // below — resetting rows here raced it (StrictMode runs this twice, and a
    // late second response wiped the rate back to 0).
    loadLiveRate(true);
  }, []);

  // Load manual silver rates (used when Metal Type = Silver)
  useEffect(() => {
    fetchSilverRates().then((data) => { if (data) setSilverRates(data); });
  }, []);

  // Rate for a purity under a given metal (silver = manual rate, no deduction)
  const rateFor = (metal, purityName) => {
    if (metal === 'SILVER') {
      const r = (silverRates?.rates || []).find((x) => x.purity === purityName);
      return r?.perGram || 0;
    }
    const rateObj = liveRates?.rates?.find((r) => r.purity === purityName);
    return getChannelRate(rateObj, 'pledge');
  };

  // Switching metal resets the items table to one blank row of that metal
  const handleMetalChange = (val) => {
    setMetalType(val);
    const defaultPurity = val === 'SILVER' ? SILVER_DEFAULT_PURITY : DEFAULT_PURITY;
    const rate = rateFor(val, defaultPurity);
    setGoldItems([recalcCaseGoldItem({
      ...EMPTY_GOLD_ITEM,
      purityName: defaultPurity,
      purity: 0,
      estimatedRatePerGram: rate,
      ratePerGram: rate,
    })]);
  };

  // Fetch GST Toggle Visibility
  useEffect(() => {
    if (user?.branch) {
      fetchBranchGstToggleVisibility(user.branch)
        .then(res => {
          if (res?.data?.data?.isVisible) {
            setShowGstToggle(true);
          }
        })
        .catch(err => console.error('Failed to fetch GST toggle visibility:', err));

      fetchBranchProcessingFeeToggleVisibility(user.branch)
        .then(res => {
          if (res?.data?.data?.isVisible !== undefined) {
            setShowProcessingFeeToggle(res.data.data.isVisible);
          }
        })
        .catch(err => console.error('Failed to fetch Processing Fee visibility:', err));
    }
  }, [user]);

  // Re-calculate defaults when deductions are loaded (for default purity)
  useEffect(() => {
    if (!deductionsLoading && !hasInitializedDefaults && liveRates?.rates && liveRates.rates.length > 0 && !isEdit) {
      const defaultRateObj = liveRates.rates.find(r => r.purity === DEFAULT_PURITY);
      // Apply deduction now that deductions are loaded
      const displayRate = getChannelRate(defaultRateObj, 'pledge');
      // Fill only rows still without a rate, so nothing already typed is lost.
      setGoldItems((prev) => prev.map((item) => (
        (item.ratePerGram || item.estimatedRatePerGram)
          ? item
          : recalcCaseGoldItem({ ...item, ratePerGram: displayRate, estimatedRatePerGram: displayRate })
      )));
      setHasInitializedDefaults(true);
    }
  }, [deductionsLoading, hasInitializedDefaults, liveRates]);

  const watchedCustomerId = Form.useWatch('customerId', form);
  const watchedAgentId = Form.useWatch('agentId', form);
  // Picking a customer fills blank bank fields from their saved primary
  // account; an edited case keeps the account it already has.
  useCustomerBankAutofill(form, selectedCustomer, { keepExisting: isEdit });

  // Resolve the selected customer in full: prefer the already-loaded list,
  // fall back to a fetch (an edited case's customer may be outside the
  // 50-row search window).
  useEffect(() => {
    if (!watchedCustomerId) return setSelectedCustomer(null);
    const fromList = customers.find((c) => c._id === watchedCustomerId);
    if (fromList) return setSelectedCustomer(fromList);
    getCustomer(watchedCustomerId)
      .then((res) => setSelectedCustomer(res.data))
      .catch(() => setSelectedCustomer(null));
  }, [watchedCustomerId, customers]);

  // Load customers for the select dropdown
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const res = await fetchCustomers({ search: customerSearch, page: 1, limit: 50 });
        setCustomers(res.data || []);
      } catch (err) {
        console.error('Error loading customers:', err);
      }
    };
    loadCustomers();
  }, [customerSearch]);

  // Load agents
  useEffect(() => {
    fetchAgents({ status: 'ACTIVE', limit: 200 })
      .then((res) => setAgents(res.data || []))
      .catch(() => {});
  }, []);

  // Load existing case if editing
  useEffect(() => {
    if (!isEdit) return;
    const loadCase = async () => {
      setLoading(true);
      try {
        const res = await getPledgeReleaseCaseById(id);
        const c = res.data;
        form.setFieldsValue({
          customerId: c.customerId?._id || c.customerId,
          lenderName: c.lenderName,
          loanAccountNumber: c.loanAccountNumber,
          status: c.status,
          principalAmount: c.principalAmount,
          interestAmount: c.interestAmount,
          penalCharges: c.penalCharges,
          closureAmount: c.closureAmount,
          totalAdvancePaid: c.totalAdvancePaid,
          closureValidTill: c.closureValidTill ? dayjs(c.closureValidTill) : null,
          withGST: c.withGST !== false,
          reasonForSelling: c.reasonForSelling,
          goldOwnership: c.verificationDetails?.goldOwnership,
          referenceDetails: c.referenceDetails,
          referencePhone: c.referencePhone,
          alternatePhone: c.alternatePhone,
          bankAccountNumber: c.bankAccountNumber,
          ifscCode: c.ifscCode,
          bankBranchName: c.bankBranchName,
        });
        setCaseMeta({ code: c.caseId || null, date: c.createdAt || null });
        setMetalType(c.metalType === 'SILVER' ? 'SILVER' : 'GOLD');
        setMetalLocked(Boolean(c.inventoryBatchId)); // metal frozen once batched
        // A locked case is only editable here under an approved reconciliation,
        // and saving spends it — worth saying so before they start typing.
        setReconciliation(
          c.reconciliation?.status === 'UNLOCKED' && c.reconciliation?.target === 'CASE_DETAILS'
            ? c.reconciliation
            : null,
        );
        if (c.goldItems && c.goldItems.length > 0) {
          // Normalize + recalc to avoid stale totals from previously saved rounding logic.
          setGoldItems(c.goldItems.map((g) => {
            const normalizedRate = g.estimatedRatePerGram ?? g.ratePerGram ?? 0;
            return recalcCaseGoldItem({
              ...g,
              purityName: g.purityName || DEFAULT_PURITY,  // Ensure purityName is set
              estimatedRatePerGram: normalizedRate,
              ratePerGram: normalizedRate,
            });
          }));
        }
        if (c.processingFee != null) {
          setProcessingFee(c.processingFee);
        }
        if (c.roundingUpAmount != null) {
          setRoundingUpAmount(c.roundingUpAmount);
        }
        if (c.agentId) {
          form.setFieldsValue({ agentId: c.agentId._id || c.agentId });
        }
        if (c.agentCommissionPercent != null) {
          setAgentCommissionPercent(c.agentCommissionPercent);
        }
      } catch (err) {
        console.error('Error loading case:', err);
        messageApi.error('Failed to load case');
      } finally {
        setLoading(false);
      }
    };
    loadCase();
  }, [id]);

  // Handle gold item changes, with auto-fill rate when karat is selected
  const handleGoldItemChange = (index, field, value) => {
    const updated = [...goldItems];
    const updatedItem = { ...updated[index], [field]: value };
    
    // Picking a karat only sets the rate — Purity (%) is always typed in by hand.
    if (field === 'purityName') {
      const displayRate = rateFor(metalType, value);
      updatedItem.estimatedRatePerGram = displayRate;
      updatedItem.ratePerGram = displayRate;
    }
    
    // Map estimatedRatePerGram ↔ ratePerGram for consistency
    if (field === 'estimatedRatePerGram') {
      updatedItem.ratePerGram = value;
    } else if (field === 'ratePerGram') {
      updatedItem.estimatedRatePerGram = value;
    }
    
    // If dustDeduction is being manually changed, mark it as overridden
    const isDustDeductionChange = field === 'dustDeduction';
    const skipDustRecalc = isDustDeductionChange;
    
    // If a non-dustDeduction field changes, clear the override flag
    if (!isDustDeductionChange) {
      updatedItem._dustDeductionOverride = false;
    } else {
      updatedItem._dustDeductionOverride = true;
    }
    
    updated[index] = recalcCaseGoldItem(updatedItem, { skipDustRecalc });
    setGoldItems(updated);
  };

  const handleAddGoldItem = () => {
    const defaultPurity = metalType === 'SILVER' ? SILVER_DEFAULT_PURITY : DEFAULT_PURITY;
    const defaultRate = rateFor(metalType, defaultPurity);
    const newItem = recalcCaseGoldItem({
      ...EMPTY_GOLD_ITEM,
      qty: goldItems.length + 1,
      purityName: defaultPurity,
      purity: 0,
      estimatedRatePerGram: defaultRate,
      ratePerGram: defaultRate,
    });
    setGoldItems([...goldItems, newItem]);
  };

  const handleRemoveGoldItem = (index) => {
    if (goldItems.length === 1) return;
    const updated = goldItems.filter((_, i) => i !== index);
    updated.forEach((item, idx) => (item.qty = idx + 1));
    setGoldItems(updated);
  };

  const totals = calculateCaseGoldTotals(goldItems);
  const effectiveProcessingFee = showProcessingFeeToggle ? processingFee : 0;
  // Gold value and fee stay exact; only the final amount rounds (nearest rupee).
  const processingFeeAmount = calculateCaseProcessingFee(totals.estimatedTotalAmount, effectiveProcessingFee);
  const approxFinalAmount = calculateCaseFinalAmount(totals.estimatedTotalAmount, processingFeeAmount, roundingUpAmount);
  const roundOffAmount = calculateCaseRoundOff(totals.estimatedTotalAmount, processingFeeAmount, roundingUpAmount, approxFinalAmount);

  const silverNotReady = metalType === 'SILVER'
    && (!silverRates || silverRates.notConfigured || !(silverRates.rates || []).some(r => r.perGram > 0));

  // ── Submit ──

  const handleSubmit = async (values) => {
    // Validate gold items
    if (!goldItems.length) {
      return messageApi.error('At least one gold item is required');
    }
    const hasInvalid = goldItems.some(
      (g) => !g.itemName || g.grossWeight <= 0 || g.purity <= 0 || (g.estimatedRatePerGram ?? g.ratePerGram ?? 0) <= 0,
    );
    if (hasInvalid) {
      return messageApi.error('Please fill all required gold item fields (name, weight, purity, rate)');
    }

    setSubmitting(true);
    try {
      const payload = {
        ...values,
        metalType,
        principalAmount: values.closureAmount || 0,
        interestAmount: 0,
        penalCharges: 0,
        closureValidTill: values.closureValidTill ? values.closureValidTill.toISOString() : null,
        processingFee: effectiveProcessingFee,
        processingFeeAmount,
        roundingUpAmount,
        approxFinalAmount,
        withGST: values.withGST,
        agentCommissionPercent: values.agentId ? agentCommissionPercent : undefined,
        goldItems: goldItems.map((g, idx) => ({
          qty: idx + 1,
          itemName: g.itemName,
          purityName: g.purityName || DEFAULT_PURITY,
          purity: g.purity,
          grossWeight: g.grossWeight,
          stoneWaxDeduction: g.stoneWaxDeduction ?? g.deduction ?? 0,
          dustDeduction: g.dustDeduction,
          netWeight: g.netWeight,
          estimatedRatePerGram: g.estimatedRatePerGram ?? g.ratePerGram ?? 0,
          totalPrice: g.totalPrice,
        })),
      };

      if (isEdit) {
        const res = await updatePledgeReleaseCase(id, payload);
        messageApi.success(res?.message || 'Case updated successfully');
      } else {
        await createPledgeReleaseCase(payload);
        allowLeave();
        messageApi.success('Case created successfully');
      }
      setTimeout(() => navigate('/pledge-release-cases'), 600);
    } catch (err) {
      console.error('Submit error:', err);
      messageApi.error(err?.response?.data?.message || 'Failed to save case');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Gold Items Table Columns ──

  const goldColumns = [
    {
      title: <span style={{ fontSize: '10px' }}>Qty</span>,
      width: 56,
      render: (_, __, index) => (
        <Space align="center" size={4} style={{ display: 'flex', whiteSpace: 'nowrap' }}>
          {/* Always rendered (hidden with one row) so the number never shifts. */}
          <Button
            type="text"
            danger
            icon={<CloseOutlined />}
            onClick={() => handleRemoveGoldItem(index)}
            size="small"
            style={{ padding: 0, fontSize: '10px', visibility: goldItems.length > 1 ? 'visible' : 'hidden' }}
            disabled={!canFieldGoldItemsEdit || goldItems.length <= 1}
          />
          <span>{index + 1}</span>
        </Space>
      ),
    },
    {
      title: <span style={{ fontSize: '10px' }}>Item Name</span>,
      dataIndex: 'itemName',
      render: (text, _, index) => (
        <Input.TextArea
          autoSize={{ minRows: 1, maxRows: 3 }}
          value={text}
          onChange={(e) => handleGoldItemChange(index, 'itemName', e.target.value)}
          size="small"
          style={{ fontSize: '10px' }}
          placeholder="e.g. Gold Chain"
          disabled={!canFieldGoldItemsEdit}
        />
      ),
    },
    {
      title: <span style={{ fontSize: '10px' }}>Purity (%)</span>,
      dataIndex: 'purity',
      width: 90,
      render: (value, _, index) => (
        <InputNumber min={0} max={100} step={1} precision={2} value={value}
          onChange={(val) => handleGoldItemChange(index, 'purity', val)}
          size="small" style={{ width: '100%', fontSize: '10px' }} controls={false} placeholder="e.g. 90"
          disabled={!canFieldGoldItemsEdit} />
      ),
    },
    {
      title: <span style={{ fontSize: '10px' }}>Est. Rate / gm (₹)</span>,
      dataIndex: 'estimatedRatePerGram',
      width: 110,
      render: (value, record, index) => (
        <InputNumber min={0} max={100000}
          value={value ?? record.ratePerGram ?? 0}
          onChange={(val) => handleGoldItemChange(index, 'estimatedRatePerGram', val)}
          size="small" style={{ width: '100%', fontSize: '10px' }} controls={false}
          disabled={!canFieldGoldItemsEdit} />
      ),
    },
    {
      title: <span style={{ fontSize: '10px' }}>Gross Weight (gm)</span>,
      dataIndex: 'grossWeight',
      width: 110,
      render: (value, _, index) => (
        <InputNumber min={0} step={0.0001} precision={4} value={value}
          onChange={(val) => handleGoldItemChange(index, 'grossWeight', val)}
          size="small" style={{ width: '100%', fontSize: '10px' }} controls={false}
          disabled={!canFieldGoldItemsEdit} />
      ),
    },
    {
      title: <span style={{ fontSize: '10px' }}>Stone/wax Deduction (gm)</span>,
      dataIndex: 'stoneWaxDeduction',
      width: 110,
      render: (value, _, index) => (
        <InputNumber min={0} step={0.0001} precision={4} value={value}
          onChange={(val) => handleGoldItemChange(index, 'stoneWaxDeduction', val)}
          size="small" style={{ width: '100%', fontSize: '10px' }} controls={false}
          disabled={!canFieldGoldItemsEdit} />
      ),
    },
    {
      title: <span style={{ fontSize: '10px' }}>Dust Deduction (gm)</span>,
      dataIndex: 'dustDeduction',
      width: 100,
      render: (value, _, index) => (
        <InputNumber min={0} step={0.0001} precision={4} value={value}
          onChange={(val) => handleGoldItemChange(index, 'dustDeduction', val)}
          size="small" style={{ width: '100%', fontSize: '10px' }} controls={false}
          disabled={!canFieldGoldItemsEdit} />
      ),
    },
    {
      title: <span style={{ fontSize: '10px' }}>Net Weight (gm)</span>,
      dataIndex: 'netWeight',
      width: 110,
      render: (value) => (
        <InputNumber value={value} precision={4} size="small" disabled
          style={{ width: '100%', fontSize: '10px', backgroundColor: 'white', color: 'black', borderColor: '#d9d9d9' }} />
      ),
    },
    {
      title: <span style={{ fontSize: '10px' }}>Total Price (₹)</span>,
      dataIndex: 'totalPrice',
      width: 150,
      render: (value) => <strong style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>₹ {formatAmount4(value)}</strong>,
    },
  ];

  return (
    <>
      {contextHolder}
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <Card>
              <Row justify="space-between" align="middle" className="mb-3">
                <Col>
                  <Title level={3} style={{ margin: 0 }}>
                    {isEdit ? 'Edit Pledge Release Case' : 'New Pledge Release Case'}
                  </Title>
                  {reconciliation && (
                    <Tag color="green" style={{ marginTop: 6 }}>
                      Unlocked by an approved reconciliation — one save
                    </Tag>
                  )}
                </Col>
                <Col>
                  <Space wrap>
                    {!isEdit && (
                      <>
                      {metalType === 'GOLD' && liveRates?.rates && Array.isArray(liveRates.rates) && (
                        (() => {
                          const rateObj = liveRates.rates.find(r => r.karat === '22k');
                          const displayRate = rateObj ? getChannelRate(rateObj, 'pledge') : 0;
                          return (
                            <Tooltip title={`Updated: ${liveRates.date} (incl. deduction)`}>
                              <Tag
                                color="gold"
                                icon={<GoldOutlined />}
                                style={{ cursor: 'default', fontSize: 13, padding: '2px 10px' }}
                              >
                                Live: {rateObj?.karat} ₹{displayRate?.toLocaleString('en-IN')}/gm
                              </Tag>
                            </Tooltip>
                          );
                        })()
                      )}
                      <Tooltip title="Refresh live gold rates and apply to all rows">
                        <Button
                          size="small"
                          icon={<SyncOutlined spin={rateLoading} />}
                          onClick={handleRefreshRate}
                          loading={rateLoading}
                        >
                          Refresh Rate
                        </Button>
                      </Tooltip>
                      </>
                    )}
                    {/* Consent to melt is taken at the counter, before the case
                        is finalised — so this prints from the live form. */}
                    <MeltingConsentButton
                      metalType={metalType}
                      customer={selectedCustomer}
                      user={user}
                      caseCode={caseMeta.code}
                      caseDate={caseMeta.date}
                      goldItems={goldItems}
                    />
                  </Space>
                </Col>
              </Row>

              <Spin spinning={loading}>
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSubmit}
                  initialValues={{
                    status: 'LEAD_IDENTIFIED',
                    penalCharges: 0,
                    totalAdvancePaid: 0,
                    withGST: true,
                  }}
                >
                                    {/* ── Gold Items Table ── */}
                  {canFieldGoldItemsShow && (
                  <>
                  <Divider orientation="left">Estimated {metalType === 'SILVER' ? 'Silver' : 'Gold'} Items</Divider>

                  <div style={{ marginBottom: 12 }}>
                    <Space align="center" wrap>
                      <Text strong>Metal Type:</Text>
                      <Radio.Group
                        value={metalType}
                        onChange={(e) => handleMetalChange(e.target.value)}
                        disabled={!canFieldGoldItemsEdit || metalLocked}
                        optionType="button"
                        buttonStyle="solid"
                        options={[{ label: 'Gold', value: 'GOLD' }, { label: 'Silver', value: 'SILVER' }]}
                      />
                      {metalType === 'SILVER' && silverRates?.date && (
                        <Tag color="default">Silver rate set: {silverRates.date} (manual)</Tag>
                      )}
                      {metalLocked && <Tag color="orange">Metal locked — already in an inventory batch</Tag>}
                    </Space>
                    {silverNotReady && (
                      <Alert
                        type="warning" showIcon style={{ marginTop: 8 }}
                        message="Silver rates are not set"
                        description="An admin must set today's silver rates in System → Silver Rates before silver items can be priced."
                      />
                    )}
                  </div>

                  <Table
                    dataSource={goldItems.map((item, idx) => ({ ...item, key: idx }))}
                    columns={goldColumns}
                    pagination={false}
                    size="small"
                    bordered
                    // Fixed layout: columns keep their width as values change (no
                    // jumping); Item Name takes the spare width, and below
                    // 1040px the table scrolls sideways instead of squeezing.
                    tableLayout="fixed"
                    scroll={{ x: 1040 }}
                    summary={() => (
                      <>
                        <Table.Summary.Row style={{ background: '#fafafa' }}>
                          <Table.Summary.Cell colSpan={4} align="right">
                            <strong style={{ fontSize: '11px' }}>Totals</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <strong style={{ fontSize: '11px' }}>{formatWeight4(totals.totalGrossWeight)} gm</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <strong style={{ fontSize: '11px' }}>{formatWeight4(totals.totalStoneDeduction)} gm</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <strong style={{ fontSize: '11px' }}>{formatWeight4(totals.totalDustDeduction)} gm</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <strong style={{ fontSize: '11px' }}>{formatWeight4(totals.totalNetWeight)} gm</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <strong style={{ fontSize: '12px' }}>₹ {formatAmount4(totals.estimatedTotalAmount)}</strong>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                        {showProcessingFeeToggle && (
                        <Table.Summary.Row>
                          <Table.Summary.Cell colSpan={8} align="right">
                            <Space>
                              <strong style={{ fontSize: '12px' }}>Processing Fee (%)</strong>
                                <InputNumber
                                  min={0}
                                  max={100}
                                  step={0.1}
                                  precision={2}
                                  value={processingFee}
                                  onChange={(val) => setProcessingFee(val || 0)}
                                  size="small"
                                  style={{ width: 80, fontSize: '10px' }}
                                  controls={false}
                                  disabled={!canFieldGoldItemsEdit}
                                />
                            </Space>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <strong style={{ fontSize: '12px' }}>{formatProcessingFeeDisplay(processingFeeAmount)}</strong>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                        )}
                        <Table.Summary.Row>
                          <Table.Summary.Cell colSpan={8} align="right">
                            <Space>
                              <strong style={{ fontSize: '12px' }}>Rounding Up (₹)</strong>
                              <InputNumber
                                max={100000}
                                step={1}
                                precision={0}
                                value={roundingUpAmount}
                                onChange={(val) => setRoundingUpAmount(val || 0)}
                                size="small"
                                style={{ width: 80, fontSize: '10px' }}
                                controls={false}
                                disabled={!canFieldGoldItemsEdit}
                              />
                            </Space>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <strong style={{ fontSize: '12px' }}>{formatRoundingAmount(roundingUpAmount)}</strong>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                        <Table.Summary.Row>
                          <Table.Summary.Cell colSpan={8} align="right">
                            <Text type="secondary" style={{ fontSize: '12px' }}>Round Off (to nearest ₹)</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <Text type="secondary" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatRoundOff(roundOffAmount)}</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                        <Table.Summary.Row>
                          <Table.Summary.Cell colSpan={8} align="right">
                            <strong style={{ fontSize: '12px' }}>Approx Final Amount</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <strong style={{ fontSize: '12px', color: '#1890ff' }}>₹ {formatWholeAmount(approxFinalAmount)}</strong>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </>
                    )}
                  />

                  <Button type="dashed" onClick={handleAddGoldItem} icon={<PlusOutlined />} style={{ marginTop: 12, marginBottom: 24 }} disabled={!canFieldGoldItemsEdit}>
                    Add Gold Item
                  </Button>
                  </>
                  )}
                  {/* Customer & Agent */}
                  <FormSection
                    title="Customer & Agent"
                    icon={<UserOutlined />}
                    description="Whose pledged gold is being released, and the referring agent"
                  >
                    <Row gutter={20}>
                      {canFieldCustomerShow && (
                        <Col xs={24} lg={12}>
                          <Form.Item name="customerId" label="Customer" rules={[{ required: true, message: 'Please select a customer' }]}>
                            <CustomerSelect
                              customers={customers}
                              onSearch={setCustomerSearch}
                              selectedCustomer={selectedCustomer}
                              disabled={!canFieldCustomerEdit}
                            />
                          </Form.Item>
                        </Col>
                      )}
                      <Col xs={24} lg={12}>
                        <Row gutter={12}>
                          <Col xs={24} sm={16}>
                            <Form.Item name="agentId" label="Agent" tooltip="Optional — leave empty when there is no referring agent.">
                              <AgentSelect
                                agents={agents}
                                onAgentChange={(agent) => setAgentCommissionPercent(agent?.defaultCommissionPercent || 0)}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={8}>
                            <Form.Item label="Commission">
                              <InputNumber
                                size="large"
                                min={0}
                                max={100}
                                step={0.1}
                                value={agentCommissionPercent}
                                onChange={(val) => setAgentCommissionPercent(val || 0)}
                                addonAfter="%"
                                style={{ width: '100%' }}
                                disabled={!watchedAgentId}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </FormSection>

                  {/* Pledged loan & closure */}
                  {(canFieldLenderShow || canFieldLoanAccountShow || canFieldClosureShow) && (
                  <FormSection
                    title="Pledged Loan"
                    icon={<BankOutlined />}
                    description="The lender's loan we are closing to release the gold"
                    extra={showGstToggle && canFieldClosureShow && (
                      <Tooltip title="Include GSTIN in generated documents.">
                        <span className="cf-gst-switch">
                          Tax details
                          <Form.Item name="withGST" valuePropName="checked" noStyle>
                            <Switch checkedChildren="With GSTIN" unCheckedChildren="Without GSTIN" />
                          </Form.Item>
                        </span>
                      </Tooltip>
                    )}
                  >
                    <Row gutter={20}>
                      {canFieldLenderShow && (
                      <Col xs={24} md={12}>
                        <Form.Item name="lenderName" label="Lender Name" rules={[{ required: true, message: 'Lender name is required' }]}>
                          <Input size="large" prefix={<BankOutlined className="text-muted" />} placeholder="e.g. Muthoot Finance" disabled={!canFieldLenderEdit} />
                        </Form.Item>
                      </Col>
                      )}
                      {canFieldLoanAccountShow && (
                      <Col xs={24} md={12}>
                        <Form.Item name="loanAccountNumber" label="Loan Account Number" rules={[{ required: true, message: 'Loan A/c number is required' }]}>
                          <Input size="large" prefix={<NumberOutlined className="text-muted" />} placeholder="e.g. GL-2024-1234567" disabled={!canFieldLoanAccountEdit} />
                        </Form.Item>
                      </Col>
                      )}
                      {canFieldClosureShow && (
                      <>
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item name="closureAmount" label="Closure Amount" rules={[{ required: true, message: 'Required' }]}>
                          <InputNumber size="large" min={0} style={{ width: '100%' }} prefix="₹" disabled={!canFieldClosureEdit} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item name="totalAdvancePaid" label="Advance Paid">
                          <InputNumber size="large" min={0} style={{ width: '100%' }} prefix="₹" disabled={!canFieldClosureEdit} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item name="closureValidTill" label="Closure Valid Till" rules={[{ required: true, message: 'Required' }]}>
                          <DatePicker size="large" format="DD MMM YYYY" style={{ width: '100%' }} disabled={!canFieldClosureEdit} />
                        </Form.Item>
                      </Col>
                      </>
                      )}
                    </Row>
                  </FormSection>
                  )}

                  <FormSection
                    title="Additional Details"
                    icon={<ProfileOutlined />}
                    description="Printed on the verification form"
                  >
                    <Row gutter={20}>
                      <Col xs={24} md={12}>
                        <Form.Item name="reasonForSelling" label="Reason for Selling Gold" rules={[{ required: true, message: 'Required' }]}>
                          <Input.TextArea rows={2} placeholder="e.g. Medical expenses, business need" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="goldOwnership" label="Gold Ownership" rules={[{ required: true, message: 'Required' }]}>
                          <Select size="large" placeholder="Select ownership" options={GOLD_OWNERSHIP_OPTIONS} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item name="referenceDetails" label="Relative / Reference Person">
                          <Input size="large" prefix={<TeamOutlined className="text-muted" />} placeholder="Name and relation" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item
                          name="referencePhone"
                          label="Reference Phone No."
                          rules={[{ pattern: /^[0-9]{10}$/, message: 'Must be a valid 10-digit number' }]}
                        >
                          <Input size="large" prefix={<PhoneOutlined className="text-muted" />} placeholder="10-digit mobile number" maxLength={10} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item
                          name="alternatePhone"
                          label="Alternate Phone No."
                          rules={[{ pattern: /^[0-9]{10}$/, message: 'Must be a valid 10-digit number' }]}
                        >
                          <Input size="large" prefix={<PhoneOutlined className="text-muted" />} placeholder="10-digit mobile number" maxLength={10} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </FormSection>

                  <FormSection
                    title="Bank Details"
                    icon={<BankOutlined />}
                    description="Where a bank settlement payout is sent"
                  >
                    <SavedBankAccountSelect form={form} customer={selectedCustomer} />
                    <Row gutter={20}>
                      <Col xs={24} md={8}>
                        <Form.Item name="bankAccountNumber" label="Account Number">
                          <Input size="large" placeholder="Bank account number" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item
                          name="ifscCode"
                          label="IFSC Code"
                          rules={[{ pattern: IFSC_PATTERN, message: 'Enter a valid IFSC, e.g. SBIN0001234' }]}
                        >
                          <IfscInput
                            size="large"
                            onResolved={(info) => form.setFieldsValue({ bankBranchName: bankBranchLabel(info) })}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item name="bankBranchName" label="Bank & Branch">
                          <Input size="large" placeholder="e.g. SBI - MG Road" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </FormSection>

                  <div className="cf-actions">
                    <Button size="large" onClick={() => navigate('/pledge-release-cases')}>Cancel</Button>
                    <Button
                      size="large"
                      type="primary"
                      htmlType="submit"
                      loading={submitting}
                      icon={<CheckOutlined />}
                      disabled={isEdit ? !canEditModule('pledgeReleaseCase') : !canViewCreateModule('pledgeReleaseCase')}
                    >
                      {isEdit ? 'Update Case' : 'Create Case'}
                    </Button>
                  </div>
                </Form>
              </Spin>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default PledgeReleaseCaseForm;
